import { useState, useEffect, useCallback } from 'react';
import type { Profile, UserWorkPercentage } from '../types';
import { useData } from '../contexts/DataContext';
import { X, Save, Percent, ChevronLeft, ChevronRight, Loader2, Check, AlertCircle } from 'lucide-react';
import { UserAvatar } from './UserAvatar';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

interface WorkPercentageModalProps {
  isOpen: boolean;
  onClose: () => void;
  employee: Profile | null;
}

export function WorkPercentageModal({
  isOpen,
  onClose,
  employee
}: WorkPercentageModalProps) {
  const { fetchUserWorkPercentages, updateUserWorkPercentages } = useData();

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [percentages, setPercentages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [quickFillValue, setQuickFillValue] = useState<string>('100');

  // Load percentages for the selected employee and year
  const loadPercentages = useCallback(async (userId: string, year: number) => {
    try {
      setLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);
      const data: UserWorkPercentage[] = await fetchUserWorkPercentages(userId);

      const map: Record<string, string> = {};
      // Initialize all 12 months with default or existing data
      for (let m = 1; m <= 12; m++) {
        const monthKey = `${year}-${String(m).padStart(2, '0')}`;
        const found = data.find(d => d.month === monthKey);
        map[monthKey] = found !== undefined ? String(found.percentage) : '';
      }
      setPercentages(map);
    } catch (err) {
      console.error(err);
      setErrorMessage('Failed to load work percentages.');
    } finally {
      setLoading(false);
    }
  }, [fetchUserWorkPercentages]);

  useEffect(() => {
    if (isOpen && employee?.id) {
      loadPercentages(employee.id, selectedYear);
    }
  }, [isOpen, employee?.id, selectedYear, loadPercentages]);

  if (!isOpen || !employee) return null;

  const handleMonthChange = (monthKey: string, value: string) => {
    setPercentages(prev => ({
      ...prev,
      [monthKey]: value
    }));
  };

  const handleApplyQuickFill = (val: string) => {
    const updated: Record<string, string> = {};
    for (let m = 1; m <= 12; m++) {
      const monthKey = `${selectedYear}-${String(m).padStart(2, '0')}`;
      updated[monthKey] = val;
    }
    setPercentages(updated);
  };

  const handleSave = async () => {
    if (!employee) return;
    setErrorMessage(null);
    setSuccessMessage(null);

    const payload: { month: string; percentage: number }[] = [];

    for (let m = 1; m <= 12; m++) {
      const monthKey = `${selectedYear}-${String(m).padStart(2, '0')}`;
      const rawVal = percentages[monthKey];
      if (rawVal !== undefined && rawVal.trim() !== '') {
        const num = parseFloat(rawVal);
        if (isNaN(num) || num < 0 || num > 100) {
          setErrorMessage(`Invalid percentage for ${MONTH_NAMES[m - 1]} (${rawVal}). Must be between 0 and 100%.`);
          return;
        }
        payload.push({
          month: monthKey,
          percentage: num
        });
      }
    }

    try {
      setSaving(true);
      await updateUserWorkPercentages(employee.id, payload);
      setSuccessMessage(`Percentages for ${selectedYear} saved successfully.`);
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3500);
    } catch (err) {
      console.error(err);
      setErrorMessage(err instanceof Error ? err.message : 'Failed to save work percentages.');
    } finally {
      setSaving(false);
    }
  };

  const employeeDisplayName = [employee.firstName, employee.lastName].filter(Boolean).join(' ') || employee.email;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100/80">
              <Percent className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Monthly Work Percentages</h2>
              <p className="text-xs text-slate-500">Configure employment workload percentage per month</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Employee & Year Toolbar */}
        <div className="px-6 py-3.5 bg-slate-50/40 border-b border-slate-200/70 flex flex-wrap items-center justify-between gap-4">
          {/* Employee Display */}
          <div className="flex items-center gap-3">
            <UserAvatar profile={employee} size="sm" />
            <div>
              <span className="text-xs font-bold text-slate-800 block">{employeeDisplayName}</span>
              <span className="text-[11px] text-slate-500">{employee.email}</span>
            </div>
          </div>

          {/* Year Picker */}
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-1 shadow-2xs">
            <button
              type="button"
              onClick={() => setSelectedYear(y => y - 1)}
              aria-label="Previous year"
              className="p-1 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-bold text-slate-700 px-2 min-w-[48px] text-center">
              {selectedYear}
            </span>
            <button
              type="button"
              onClick={() => setSelectedYear(y => y + 1)}
              aria-label="Next year"
              className="p-1 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition cursor-pointer"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Quick Fill Toolbar */}
        <div className="px-6 py-2.5 bg-indigo-50/50 border-b border-indigo-100/60 flex flex-wrap items-center justify-between gap-3 text-xs">
          <span className="text-slate-600 font-medium">Quick set all 12 months:</span>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0"
                max="100"
                value={quickFillValue}
                onChange={e => setQuickFillValue(e.target.value)}
                placeholder="100"
                className="w-16 px-2 py-1 text-xs text-right font-medium rounded-lg border border-indigo-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              />
              <span className="text-slate-500 font-semibold">%</span>
              <button
                type="button"
                onClick={() => handleApplyQuickFill(quickFillValue)}
                className="px-2.5 py-1 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition cursor-pointer shadow-2xs text-[11px]"
              >
                Apply
              </button>
            </div>
            <span className="text-slate-300">|</span>
            <div className="flex items-center gap-1">
              {['100', '80', '50'].map(preset => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => handleApplyQuickFill(preset)}
                  className="px-2 py-0.5 rounded-md border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50 font-medium text-[11px] transition cursor-pointer"
                >
                  {preset}%
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Feedback Messages */}
        {errorMessage && (
          <div className="px-6 py-2.5 bg-rose-50 border-b border-rose-200 text-rose-700 text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}
        {successMessage && (
          <div className="px-6 py-2.5 bg-emerald-50 border-b border-emerald-200 text-emerald-700 text-xs flex items-center gap-2">
            <Check className="w-4 h-4 shrink-0" />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Content: 12 Months Grid */}
        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              <span className="text-xs">Loading work percentages...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {MONTH_NAMES.map((name, index) => {
                const monthNum = index + 1;
                const monthKey = `${selectedYear}-${String(monthNum).padStart(2, '0')}`;
                const val = percentages[monthKey] ?? '';

                return (
                  <div
                    key={monthKey}
                    className="p-3 rounded-xl border border-slate-200/80 bg-slate-50/50 hover:bg-slate-50 transition flex items-center justify-between gap-2"
                  >
                    <div>
                      <span className="text-xs font-semibold text-slate-800 block">
                        {name}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono">
                        {monthKey}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        aria-label={`Percentage for ${name} ${selectedYear}`}
                        value={val}
                        onChange={e => handleMonthChange(monthKey, e.target.value)}
                        placeholder="100"
                        className="w-18 px-2 py-1.5 text-xs text-right font-medium rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none shadow-2xs"
                      />
                      <span className="text-xs font-semibold text-slate-500">%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition cursor-pointer"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold text-xs shadow-xs transition disabled:opacity-50 cursor-pointer"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>Save Percentages</span>
          </button>
        </div>
      </div>
    </div>
  );
}

